import { useState, useEffect } from 'react'
import { Spinner, Message } from '../../index'
import {
  InputText,
  InputTextArea,
  InputMultipleCheckBox,
} from '../../../utils/dynamicForm'

const FormRoles = ({
  edit,
  formCleanHandler,
  isLoading,
  register,
  isError,
  errors,
  isLoadingUpdate,
  isLoadingPost,
  handleSubmit,
  submitHandler,
  error,
  permissionData,
  clientPermissionData,
  roles,
  currentRoleId,
  copyPermissionsFromRole,
}) => {

  const [templateRoleId, setTemplateRoleId] = useState('');

  // Reset the template picker whenever the modal switches between add/edit or
  // to a different role, so a stale selection never lingers.
  useEffect(() => {
    setTemplateRoleId('');
  }, [edit, currentRoleId]);

  // Offer every role except the one currently being edited as a copy source.
  const templateRoles = (roles || [])
    .filter((role) => role._id !== currentRoleId)
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const templateChangeHandler = (e) => {
    const roleId = e.target.value;
    setTemplateRoleId(roleId);
    if (roleId && copyPermissionsFromRole) {
      copyPermissionsFromRole(roleId);
    }
  };

  const sortedClientPermissionData = clientPermissionData && clientPermissionData.sort((a, b) => {
    return a.menu.localeCompare(b.menu);
  });

  // Group permissions by their group field; ungrouped go under 'Other'
  const permissionGroups = (() => {
    if (!permissionData) { return []; }
    const map = {};
    const sorted = [...permissionData].sort((a, b) => a.method.localeCompare(b.method));
    for (const item of sorted) {
      const key = item.group?.trim() || 'Other';
      if (!map[key]) { map[key] = []; }
      map[key].push(item);
    }
    // Sort groups alphabetically, but keep 'Other' last
    return Object.entries(map).sort(([a], [b]) => {
      if (a === 'Other') { return 1; }
      if (b === 'Other') { return -1; }
      return a.localeCompare(b);
    });
  })();

  const GROUP_COLORS = [
    '#e3f2fd', '#f3e5f5', '#e8f5e9', '#fff8e1',
    '#fce4ec', '#e0f7fa', '#f1f8e9', '#ede7f6',
  ];

  return (
    <div
      className='modal fade'
      id='roleModal'
      data-bs-backdrop='static'
      data-bs-keyboard='false'
      tabIndex='-1'
      aria-labelledby='roleModalLabel'
      aria-hidden='true'
    >
      <div className='modal-dialog modal-lg'>
        <div className='modal-content modal-background'>
          <div className='modal-header'>
            <h3 className='modal-title ' id='roleModalLabel'>
              {edit ? 'Edit Role' : 'Post Role'}
            </h3>
            <button
              type='button'
              className='btn-close'
              data-bs-dismiss='modal'
              aria-label='Close'
              onClick={formCleanHandler}
            ></button>
          </div>
          <div className='modal-body'>
            {isLoading ? (
              <Spinner />
            ) : isError ? (
              <Message variant='danger'>{error}</Message>
            ) : (
              <form onSubmit={handleSubmit(submitHandler)}>
                {/* Rendered as JSX elements (not function calls) so the
                    useTranslation() hook inside each lives in its own component
                    scope — otherwise toggling the isLoading branch changes the
                    parent's hook count and throws React error #310. */}
                <InputText
                  register={register}
                  errors={errors}
                  label='Name'
                  name='name'
                  placeholder='Name'
                />

                <InputTextArea
                  register={register}
                  errors={errors}
                  label='Description'
                  name='description'
                  placeholder='Description'
                />

                {templateRoles.length > 0 && (
                  <div className='border border-secondary p-2 mb-2'>
                    <label className='roleLabel' htmlFor='templateRole'>
                      Copy permissions from role (template)
                    </label>
                    <select
                      id='templateRole'
                      className='form-select'
                      value={templateRoleId}
                      onChange={templateChangeHandler}
                    >
                      <option value=''>— Select a role to copy from —</option>
                      {templateRoles.map((role) => (
                        <option key={role._id} value={role._id}>
                          {role.name}
                          {' ('}
                          {(role.permission?.length || 0) + (role.clientPermission?.length || 0)}
                          {' permissions)'}
                        </option>
                      ))}
                    </select>
                    <small className='text-muted'>
                      Pre-fills the checkboxes below with the selected role&apos;s permissions.
                      You can still tweak them before saving.
                    </small>
                  </div>
                )}

                <div className='border border-secondary p-2'>
                  <label className="roleLabel" htmlFor='permission'>API Permission</label>

                  {permissionGroups.map(([groupName, items], groupIdx) => (
                    <div
                      key={groupName}
                      style={{
                        background: GROUP_COLORS[groupIdx % GROUP_COLORS.length],
                        border: '1px solid #dee2e6',
                        borderRadius: 6,
                        marginBottom: 10,
                        padding: '8px 10px',
                      }}
                    >
                      <div style={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: '#555',
                        marginBottom: 8,
                        paddingBottom: 4,
                        borderBottom: '1px solid #ccc',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}>
                        <span>{groupName}</span>
                        <span style={{ fontWeight: 400, opacity: 0.6 }}>{items.length}</span>
                      </div>
                      <div className='row g-1'>
                        {items.map((item) => {
                          // Public endpoints (auth === false) bypass auth entirely
                          // on the server, so they're accessible to everyone no
                          // matter the role — a per-role on/off toggle is
                          // meaningless. Show them as a fixed green "enabled"
                          // switch that can't be turned off, instead of a toggle.
                          const isPublic = !item.auth;
                          return (
                          <div
                            key={item._id}
                            className='col-md-4 col-6'
                            title={`${item.route}\n${item.description}`}
                          >
                            <div className='form-check form-switch'>
                              {isPublic ? (
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked
                                  disabled
                                  readOnly
                                  style={{
                                    backgroundColor: '#198754',
                                    borderColor: '#198754',
                                    opacity: 1,
                                    cursor: 'not-allowed',
                                  }}
                                  id={`flexCheck${item._id}`}
                                  title='Public endpoint — accessible to everyone, cannot be disabled'
                                />
                              ) : (
                                <input
                                  {...register('permission')}
                                  className='form-check-input'
                                  type='checkbox'
                                  value={item._id}
                                  id={`flexCheck${item._id}`}
                                />
                              )}
                              <label
                                className='form-check-label'
                                style={{ fontSize: '0.78rem' }}
                                htmlFor={`flexCheck${item._id}`}
                              >
                                {item.method} - {item.description}
                                {isPublic && (
                                  <span className='text-success fw-semibold'> (public)</span>
                                )}
                              </label>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className='border border-secondary p-2 mt-2'>
                  <label className="roleLabel" htmlFor='clientPermission'>Client Permission</label>
                  <InputMultipleCheckBox
                    register={register}
                    errors={errors}
                    label='Client Permission'
                    name='clientPermission'
                    placeholder='Client Permission'
                    data={
                        sortedClientPermissionData &&
                        sortedClientPermissionData.map((item) => ({
                          name: `${item.menu} - ${item.path}`,
                          _id: item._id,
                        }))
                    }
                    isRequired={false}
                  />
                </div>

                <div className='modal-footer'>
                  <button
                    type='button'
                    className='btn btn-secondary '
                    data-bs-dismiss='modal'
                    onClick={formCleanHandler}
                  >
                    Close
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary '
                    disabled={isLoadingPost || isLoadingUpdate}
                  >
                    {isLoadingPost || isLoadingUpdate ? (
                      <span className='spinner-border spinner-border-sm' />
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FormRoles
